import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch
} from "react-router-dom";

import { RoleProtectedRoute } from "../ProtectedRoute";
import { useApi } from "../../api";
import Table from "./Table";
import List from "./List";
import Pagination from "../../elements/Pagination";
import SearchTermFilter from "./Search/SearchTermFilter";
import { useWhitelabel } from "../../domain/whitelabel";
import Export from "../Export";
import ConfigDialog from "./Config/ConfigDialog";
import BlockQueries from "../BlockQueries";
import RefDateQueries from "../RefDateQueries";
import Create from "../Devices/Create";
import PageSelect from "../../elements/PageSelect";
import { ROLES } from "../../auth";
import { useDeviceIds } from "../../domain/device";
import MapDialog from "./Map/MapDialog";
import EmptyState from "./EmptyState";

const ListContainer = () => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const api = useApi();
  const {
    whitelabelConfig,
    whitelabelConfigLoaded,
    updateWhitelabelConfig
  } = useWhitelabel();
  const { reload: reloadDeviceIds } = useDeviceIds();

  const [devices, setDevices] = useState([]);
  const [sort, setSort] = useState({ by: null, desc: false });
  const [totalCount, setTotalCount] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [tableColumns, setTableColumns] = useState([]);

  const [sensorSettings, setSensorSettings] = useState({
    showNames: false,
    showVisuals: false,
    showUnits: false,
    showPercentages: false,
    showTemperatures: false
  });

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [readyToLoadDevices, setReadyToLoadDevices] = useState(false);

  const [advancedFilters, setAdvancedFilters] = useState({
    medium: "",
    status: ""
  });

  const [isLoading, setLoading] = useState(true);
  const [isUpdating, setUpdating] = useState(false);

  const groupByUser = whitelabelConfig["ui.devices.groupByUser"];

  const handleSetSearchTerm = useCallback(term => {
    setSearchTerm(prev => {
      if (prev === term) return prev;
      setPage(1);
      return term;
    });
  }, []);

  const handleSetTypeFilter = useCallback(type => {
    setTypeFilter(prev => {
      if (prev === type) return prev;
      setPage(1);
      return type;
    });
  }, []);

  const handleSetAdvancedFilters = useCallback(filters => {
    setAdvancedFilters(prev => {
      if (
        prev.medium === (filters.medium || "") &&
        prev.status === (filters.status || "")
      ) {
        return prev;
      }
      return {
        medium: filters.medium || "",
        status: filters.status || ""
      };
    });
  }, []);

  const handleSelectPerPage = useCallback(
    perPageValue => {
      const value = parseInt(perPageValue, 10);
      updateWhitelabelConfig("ui.devices.list.perPage", value);
      setPerPage(value);
      setPage(1);
    },
    [updateWhitelabelConfig]
  );

  const handleUpdateSort = useCallback(
    by => {
      setSort(prevSort => {
        let desc = false;
        if (prevSort.by === by && prevSort.desc === false) {
          desc = true;
        }

        updateWhitelabelConfig("ui.devices.sort.by", by);
        updateWhitelabelConfig("ui.devices.sort.desc", desc);
        reloadDeviceIds();

        return { by, desc };
      });
    },
    [updateWhitelabelConfig, reloadDeviceIds]
  );

  // prevent sort state from being initialized multiple times
  const sortInitialized = useRef(false);

  useEffect(() => {
    setPage(1);
  }, [groupByUser]);

  useEffect(() => {
    if (!whitelabelConfigLoaded) return;

    // Only initialize sort once
    if (!sortInitialized.current) {
      setSort({
        by: whitelabelConfig["ui.devices.sort.by"],
        desc: whitelabelConfig["ui.devices.sort.desc"]
      });
      sortInitialized.current = true;
    }

    setPerPage(whitelabelConfig["ui.devices.list.perPage"]);

    setTableColumns(prev => {
      const newColumns = whitelabelConfig["ui.devices.list.columns"] || [];
      if (JSON.stringify(prev) === JSON.stringify(newColumns)) {
        return prev;
      }
      return newColumns;
    });

    // only update if changed
    setSensorSettings(prev => {
      const newSettings = {
        showNames: whitelabelConfig["ui.devices.list.sensors.showNames"],
        showVisuals: whitelabelConfig["ui.devices.list.sensors.showVisuals"],
        showUnits: whitelabelConfig["ui.devices.list.sensors.showUnits"],
        showPercentages:
          whitelabelConfig["ui.devices.list.sensors.showPercentages"],
        showTemperatures:
          whitelabelConfig["ui.devices.list.sensors.showTemperatures"]
      };

      // Deep comparison
      const hasChanged = Object.keys(newSettings).some(
        key => prev[key] !== newSettings[key]
      );

      return hasChanged ? newSettings : prev;
    });

    setReadyToLoadDevices(true);
  }, [whitelabelConfig, whitelabelConfigLoaded]);

  // prevent unneeded API calls
  const queryParams = useMemo(() => {
    let query = "";

    if (sort.by) {
      query += `&sort=${sort.by}:${sort.desc ? "desc" : "asc"}`;
    }

    if (page) {
      query += `&page=${page}`;
    }

    if (perPage) {
      query += `&per_page=${perPage}`;
    }

    if (searchTerm) {
      query += `&search_term=${encodeURI(searchTerm)}`;
    }

    if (typeFilter) {
      query += `&search_term=${encodeURI(typeFilter)}`;
    }

    if (groupByUser) {
      query += "&grouped=1";
    }

    return query;
  }, [sort.by, sort.desc, page, perPage, searchTerm, typeFilter, groupByUser]);

  useEffect(() => {
    if (!readyToLoadDevices) return;

    setUpdating(true);

    api.get(`/v2/devices?${queryParams}`).then(response => {
      setDevices(response.data);
      setTotalCount(response.headers["x-total-count"]);
      setShowMap(response.headers["x-location-enabled-devices-count"] > 0);
      setLoading(false);
      setUpdating(false);
    });
  }, [api, queryParams, readyToLoadDevices]);

  const normalize = useCallback(
    v => (typeof v === "string" ? v.toLowerCase() : ""),
    []
  );

  const matchesMedium = useCallback(
    (device, medium) => {
      if (!medium) return true;
      const m = normalize(medium);
      if (!Array.isArray(device.sensors)) return false;

      return device.sensors.some(
        s => s && s.contentType && normalize(s.contentType).includes(m)
      );
    },
    [normalize]
  );

  const matchesStatus = useCallback(
    (device, status) => {
      if (!status) return true;
      const s = normalize(status);
      return (
        device.status &&
        (normalize(device.status) === s || normalize(device.status).includes(s))
      );
    },
    [normalize]
  );

  // memoize filtered devices
  const filteredDevices = useMemo(() => {
    if (!advancedFilters.medium && !advancedFilters.status) {
      return devices;
    }

    return devices.filter(
      d =>
        matchesMedium(d, advancedFilters.medium) &&
        matchesStatus(d, advancedFilters.status)
    );
  }, [
    devices,
    advancedFilters.medium,
    advancedFilters.status,
    matchesMedium,
    matchesStatus
  ]);

  const hasActiveFilters = useMemo(
    () =>
      !!(
        searchTerm ||
        typeFilter ||
        advancedFilters.medium ||
        advancedFilters.status
      ),
    [searchTerm, typeFilter, advancedFilters.medium, advancedFilters.status]
  );

  // memoize pagination component
  const paginationComponent = useMemo(
    () => (
      <Pagination
        totalCount={totalCount}
        perPage={perPage}
        page={page}
        onSelectPage={setPage}
        allowPerPageSelect={false}
      />
    ),
    [totalCount, perPage, page]
  );

  const pageSelectComponent = useMemo(
    () => (
      <PageSelect
        perPage={perPage}
        perPageOptions={[10, 25, 50, 100]}
        onSelectPerPage={handleSelectPerPage}
      />
    ),
    [perPage, handleSelectPerPage]
  );

  const tableComponent = useMemo(() => {
    if (filteredDevices.length === 0) {
      return <EmptyState hasFilters={hasActiveFilters} />;
    }

    return (
      <Table
        devices={filteredDevices}
        sort={sort}
        onUpdateSort={handleUpdateSort}
        groupByUser={groupByUser}
        isUpdating={isUpdating}
        tableColumns={tableColumns}
        sensorSettings={sensorSettings}
      />
    );
  }, [
    filteredDevices,
    hasActiveFilters,
    sort,
    handleUpdateSort,
    groupByUser,
    isUpdating,
    tableColumns,
    sensorSettings
  ]);

  const searchComponent = useMemo(
    () => (
      <SearchTermFilter
        onSetSearchTerm={handleSetSearchTerm}
        onSetTypeFilter={handleSetTypeFilter}
        onSetAdvancedFilters={handleSetAdvancedFilters}
      />
    ),
    [handleSetSearchTerm, handleSetTypeFilter, handleSetAdvancedFilters]
  );

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Switch>
        <RoleProtectedRoute role={ROLES.ADMIN} path={`${url}/new`}>
          <Create onClose={() => history.push("/devices")} />
        </RoleProtectedRoute>
        <Route path={`${url}/config`}>
          <ConfigDialog onClose={() => history.push("/devices")} />
        </Route>
        <Route path={`${url}/map`}>
          <MapDialog onClose={() => history.push("/devices")} />
        </Route>
        <Route path={`${url}/export`}>
          <Export onClose={() => history.push("/devices")} />
        </Route>
        <Route path={`${url}/block-queries`}>
          <BlockQueries onClose={() => history.push("/devices")} />
        </Route>
        <RoleProtectedRoute role={ROLES.USER} path={`${url}/ref-date-queries`}>
          <RefDateQueries onClose={() => history.push("/devices")} />
        </RoleProtectedRoute>
        <Route>
          <Redirect to={url} />
        </Route>
      </Switch>

      <List
        onShowExport={() => history.push("/devices/export")}
        onShowConfig={() => history.push("/devices/config")}
        onShowMap={() => history.push("/devices/map")}
        onShowBlockQueries={() => history.push("/devices/block-queries")}
        onShowRefDateQueries={() => history.push("/devices/ref-date-queries")}
        onShowCreate={() => history.push("/devices/new")}
        showMap={showMap}
        count={filteredDevices.length}
        totalCount={totalCount}
        paginationComponent={paginationComponent}
        pageSelectComponent={pageSelectComponent}
        tableComponent={tableComponent}
        searchComponent={searchComponent}
      />
    </>
  );
};

export default ListContainer;
